// Deals & Sales Pipeline — types mirror the backend `dealSchema` and the
// /api/deals contracts exactly. No invented fields.

export type DealStage =
  | 'new_lead'
  | 'contacted'
  | 'qualified'
  | 'requirement_received'
  | 'quotation_sent'
  | 'follow_up'
  | 'negotiation'
  | 'sample_trial'
  | 'trial_successful'
  | 'trial_unsuccessful'
  | 'po_expected'
  | 'order_confirmed'
  | 'production'
  | 'dispatch'
  | 'payment_pending'
  | 'closed_won'
  | 'closed_lost'
  // Legacy stages still accepted during migration
  | 'prospecting'
  | 'qualification'
  | 'proposal'
  | (string & {});

export type DealPriority = 'low' | 'medium' | 'high';

/** Currencies offered in the UI. INR is the domestic currency. */
export type DealCurrency = 'INR' | 'USD' | 'EUR' | 'AED' | 'GBP';

/**
 * Populated assignee shape returned by the list endpoint
 * (`Deals.list` populates assigned_to → { _id, display_name }).
 * The detail endpoint returns the raw id string.
 */
export type AssignedRef = string | { _id?: string; id?: string; display_name?: string } | null;

export interface Deal {
  id: string;
  title: string;
  contact_name?: string;
  phone?: string;
  email?: string;
  business_name?: string;
  service_required?: string;
  additional_info?: string;
  attachments?: unknown[];
  priority?: DealPriority;
  contact_id?: string | null; // integration point: Contacts module (Phase 2)
  /**
   * @deprecated Removed by the Leads + Deals merge. A Deal used to be a second
   * document pointing back at the Lead it came from; there is one record now,
   * so nothing is ever written here and nothing should read it. Kept only so
   * code still referencing it fails at the type level rather than silently
   * reading `undefined` at runtime. Delete once nothing mentions it.
   */
  lead_id?: never;
  source?: string;
  value?: number;
  grandTotal?: number;
  /** May hold older imported values; display them defensively. */
  currency?: string;
  stage: DealStage;
  probability?: number;
  expected_close?: string | null;
  assigned_to?: AssignedRef;
  assigned_name?: string;
  creator_name?: string;
  created_by?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
  /** Mapped client-side from /api/ai/deal-scores by dealId (never cross-user). */
  ai_score?: number;
  // ── CRM 2.0 — deal hand-off / assignment acceptance (additive) ──────────────
  /** Who handed the deal to the current assignee (id). Name resolved client-side. */
  assigned_by?: string | null;
  assigned_at?: string | null;
  /** 'none' = no pending hand-off; otherwise the assignee's response state. */
  assignment_status?: AssignmentStatus;
  assignment_note?: string;
  assignment_responded_at?: string | null;
  // ── Section 04 Integration — Financial Sync (Accounts) ──────────────────────
  paymentStatus?: 'paid' | 'partially_paid' | 'unpaid' | 'overdue' | null;
  lastPaymentAt?: string | null;
  timeline?: {
    type: string;
    invoiceId?: string;
    amountPaid?: number;
    currency?: string;
    status?: string;
    occurredAt: string;
    note?: string;
    createdAt: string;
  }[];
}

export type AssignmentStatus = 'none' | 'pending' | 'accepted' | 'rejected';

export interface PipelineStat {
  stage: string;
  count: number;
  total_value: number;
}

export interface DealsFilters {
  stage?: string;
  source?: string;
  assignedTo?: string;
  contact_id?: string;
  /**
   * BizPlus parity (Phase 0.3). Normally left unset — useDeals merges in the
   * Topbar branch switcher's choice automatically. Set explicitly to override
   * that for one call.
   */
  branchId?: string;
}

export interface CreateDealPayload {
  title: string;
  contact_name?: string;
  phone?: string;
  email?: string;
  business_name?: string;
  service_required?: string;
  additional_info?: string;
  priority?: DealPriority;
  contact_id?: string | null;
  source?: string;
  value?: number;
  currency?: DealCurrency;
  stage?: DealStage;
  probability?: number;
  expected_close?: string | null;
  assigned_to?: string | null;
  notes?: string;
  additionalInfoValues?: Array<{ fieldId: string; key: string; value: string }>;
}

export type UpdateDealPayload = Partial<CreateDealPayload>;

export interface UserOption {
  id: string;
  display_name: string;
  role: string;
  department?: string;
  username?: string;
}

/** Shape from GET /api/ai/deal-scores. */
export interface DealScore {
  dealId: string;
  score: number;
  stage: string;
  factors?: { label: string; value: unknown; impact: number }[];
}

/** Normalize the assignee reference to a plain id string. */
export function getAssignedId(a: AssignedRef | undefined): string {
  if (!a) return '';
  if (typeof a === 'string') return a;
  return a.id ?? a._id ?? '';
}
